import React, { useState } from 'react';
import { Modal, Form, Input, Alert, message, Steps } from 'antd';
import { KeyOutlined, SafetyOutlined } from '@ant-design/icons';
import { setFundPassword, verifyAdminPassword } from '@/api/returnRequest';

/**
 * 资金安全密码设置/修改弹窗（两步式）
 * 第一步：校验当前管理员登录密码（防止会话被劫持后随意改动资金密码）
 * 第二步：设置资金安全密码（密码规则由后端强制校验：8-72 位，须包含字母、数字和特殊字符）
 */
const FundPasswordModal = ({ open, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [step, setStep] = useState(0);
  const [stepUpToken, setStepUpToken] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const resetAll = () => {
    form.resetFields();
    setStep(0);
    setStepUpToken('');
  };

  const handleClose = () => {
    resetAll();
    onClose?.();
  };

  // 第一步：校验管理员登录密码
  const handleVerifyAdmin = async () => {
    try {
      const { adminPassword } = await form.validateFields(['adminPassword']);
      setVerifying(true);
      const res = await verifyAdminPassword(adminPassword);
      const token = res?.data?.token;
      if (!token) throw new Error(res?.message || '管理员身份验证失败');
      setStepUpToken(token);
      setStep(1);
      message.success('管理员身份已确认，请设置资金安全密码');
    } catch (error) {
      if (error?.errorFields) return;
      const msg = error?.response?.data?.message || error?.message || '管理员密码验证失败';
      message.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  // 第二步：保存资金安全密码（携带 step-up token）
  const handleSaveFundPassword = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await setFundPassword(values.password, stepUpToken);
      message.success('资金安全密码已保存，退款时请使用该密码验证');
      resetAll();
      onClose?.();
      onSuccess?.();
    } catch (error) {
      if (error?.errorFields) return;
      const msg = error?.response?.data?.message || error?.message || '设置失败';
      // step-up 凭证过期（5 分钟）时回到第一步重新验证
      if (msg.includes('已失效') || msg.includes('重新验证')) {
        message.warning('身份验证已过期，请重新输入管理员密码');
        setStep(0);
        setStepUpToken('');
        return;
      }
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOk = step === 0 ? handleVerifyAdmin : handleSaveFundPassword;

  return (
    <Modal
      title={
        <span>
          <KeyOutlined style={{ marginRight: 8 }} />
          设置资金安全密码
        </span>
      }
      open={open}
      onOk={handleOk}
      onCancel={handleClose}
      okText={step === 0 ? '下一步' : '保存'}
      cancelText="取消"
      confirmLoading={step === 0 ? verifying : submitting}
      destroyOnClose
    >
      <Steps
        size="small"
        current={step}
        style={{ marginBottom: 20 }}
        items={[
          { title: '管理员确认' },
          { title: '设置密码' },
        ]}
      />

      {step === 0 && (
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="为保障资金安全，设置资金安全密码前需验证当前管理员登录密码"
          />
          <Form form={form} layout="vertical">
            <Form.Item
              name="adminPassword"
              label="管理员登录密码"
              rules={[{ required: true, message: '请输入当前管理员的登录密码' }]}
              extra="即登录管理后台所使用的密码，用于确认是本人操作"
            >
              <Input.Password
                autoComplete="current-password"
                placeholder="请输入管理员密码"
                visibilityToggle
                onPressEnter={handleVerifyAdmin}
                prefix={<SafetyOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              />
            </Form.Item>
          </Form>
        </>
      )}

      {step === 1 && (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="该密码用于退款等资金操作的二次确认，与管理后台登录密码相互独立"
          />
          <Form form={form} layout="vertical">
            <Form.Item
              name="password"
              label="资金安全密码"
              rules={[
                { required: true, message: '请输入资金安全密码' },
                { min: 8, max: 72, message: '长度需为 8-72 位' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const hasLetter = /[A-Za-z]/.test(value);
                    const hasDigit = /\d/.test(value);
                    const hasSpecial = /[^A-Za-z0-9]/.test(value);
                    if (!hasLetter || !hasDigit || !hasSpecial) {
                      return Promise.reject(new Error('必须同时包含字母、数字和特殊字符'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              extra="8-72 位，须包含字母、数字和特殊字符，请妥善保管"
            >
              <Input.Password
                autoComplete="new-password"
                placeholder="请输入新密码"
                visibilityToggle
              />
            </Form.Item>
            <Form.Item
              name="confirm"
              label="确认密码"
              dependencies={['password']}
              rules={[
                { required: true, message: '请再次输入密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  },
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" placeholder="请再次输入" />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default FundPasswordModal;
